<template>
  <UBadge
    :as="as"
    v-bind="$attrs"
    :label="label"
    :color="color"
    :variant="variant"
    :size="size"
    :square="square"
    :avatar="avatar"
    class="p-badge select-none"
    :class="props.class"
  >
    <template v-if="leadingBadgeIcon" #leading="{ ui }">
      <FontAwesomeIcon :icon="leadingBadgeIcon" aria-hidden="true" :class="ui.leadingIcon()" />
    </template>
    <template v-if="$slots.default" #default><slot /></template>
    <template v-if="trailingBadgeIcon" #trailing="{ ui }">
      <FontAwesomeIcon :icon="trailingBadgeIcon" aria-hidden="true" :class="ui.trailingIcon()" />
    </template>
  </UBadge>
</template>

<script setup lang="ts">
import type { ComponentColor } from "@/config/component-colors"
import { computed, type VNode } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import UBadge, { type BadgeProps } from "@nuxt/ui/components/Badge.vue"

type BadgeColor = ComponentColor
type BadgeIcon = [string, string]

interface Props extends Omit<BadgeProps, "ui" | "color" | "icon" | "leadingIcon" | "trailingIcon"> {
  color?: BadgeColor
  /** FontAwesome icon positioned using leading and trailing. */
  icon?: BadgeIcon
  /** FontAwesome icon before the content. */
  leadingIcon?: BadgeIcon
  /** FontAwesome icon after the content. */
  trailingIcon?: BadgeIcon
}

const props = withDefaults(defineProps<Props>(), {
  as: "span",
  color: "primary",
  variant: "solid",
  size: "md",
  trailing: undefined
})

defineOptions({ inheritAttrs: false })
defineSlots<{ default?(): VNode[] }>()

const leadingBadgeIcon = computed(
  () =>
    (props.leadingIcon ||
      (props.icon && (props.leading || !props.trailing) ? props.icon : undefined)) as
      IconProp | undefined
)
const trailingBadgeIcon = computed(
  () =>
    (props.icon && props.trailing
      ? props.trailingIcon || props.icon
      : props.trailing !== false
        ? props.trailingIcon
        : undefined) as IconProp | undefined
)
</script>
